import React, { useState } from 'react';
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useVelocity,
} from 'framer-motion';
import { collective, Project } from '../content';
import { EASE_OUT } from './motion';

function Row({
  project,
  index,
  active,
  onEnter,
}: {
  project: Project;
  index: number;
  active: number | null;
  onEnter: (i: number) => void;
}) {
  const dimmed = active !== null && active !== index;
  const content = (
    <>
      <span className="row__no mono muted">{String(index + 1).padStart(2, '0')}</span>
      <span className="row__name">
        {project.name}
        {project.href && <span className="row__arrow">↗</span>}
      </span>
      <span className="row__note serif">{project.note}</span>
      <span className="row__field mono">{project.field}</span>
      <span className="row__year mono muted">{project.year}</span>
    </>
  );

  return (
    <motion.li
      className={`row${dimmed ? ' row--dim' : ''}${active === index ? ' row--active' : ''}`}
      onPointerEnter={() => onEnter(index)}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -5% 0px' }}
      transition={{ duration: 0.9, ease: EASE_OUT, delay: index * 0.05 }}
    >
      {project.href ? (
        <a
          className="row__inner"
          href={project.href}
          target="_blank"
          rel="noopener noreferrer"
          data-cursor-hide
          onFocus={() => onEnter(index)}
        >
          {content}
        </a>
      ) : (
        <div className="row__inner" data-cursor-hide>
          {content}
        </div>
      )}
    </motion.li>
  );
}

/** The studio's index: every field and project, with a typographic preview that trails the cursor. */
export function Collective() {
  const [active, setActive] = useState<number | null>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 26 });
  const sy = useSpring(y, { stiffness: 220, damping: 26 });
  const rotate = useTransform(useVelocity(sx), [-2000, 0, 2000], [-10, 0, 10], { clamp: true });

  const project = active !== null ? collective[active] : null;

  return (
    <section id="collective" className="collective wrap">
      <div className="section-head mono">
        <span>(03)</span>
        <span>The Collective</span>
      </div>
      <p className="collective__intro">
        One studio, many rooms. <span className="serif muted">Code, cause, craft &amp; home.</span>
      </p>

      <div className="collective__labels mono muted" aria-hidden="true">
        <span>No.</span>
        <span>Project</span>
        <span>Field</span>
        <span>Status</span>
      </div>

      <ul
        className="collective__list"
        onPointerMove={(e) => {
          x.set(e.clientX);
          y.set(e.clientY);
        }}
        onPointerLeave={() => setActive(null)}
      >
        {collective.map((p, i) => (
          <Row key={p.name} project={p} index={i} active={active} onEnter={setActive} />
        ))}
      </ul>

      <motion.div className="preview" style={{ x: sx, y: sy, rotate }} aria-hidden="true">
        <AnimatePresence>
          {project && (
            <motion.div
              className="preview__card"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7 }}
              transition={{ duration: 0.35, ease: EASE_OUT }}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.div
                  key={project.name}
                  className="preview__content"
                  initial={{ y: '100%' }}
                  animate={{ y: '0%' }}
                  exit={{ y: '-100%' }}
                  transition={{ duration: 0.5, ease: EASE_OUT }}
                >
                  <span className="preview__glyph serif">{project.name.charAt(0)}</span>
                  <span className="mono">{project.field}</span>
                  <span className="mono preview__go">{project.href ? 'Visit ↗' : project.year}</span>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
